import {
  BREAKDOWN_TAB_VALUES,
  type BreakdownTab,
  DEFAULT_BREAKDOWN_TAB,
} from '@/components/dashboard/activePositionsChart'
import { DashboardPage } from '@/components/dashboard/dashboardPage'
import { getPositionsAboveThreshold } from '@/components/dashboard/getPositionsAboveThreshold'
import { useGroupedBalances } from '@/components/dashboard/useGroupedBalances/useGroupedBalances'
import { useGroupedPositions } from '@/components/dashboard/useGroupedBalances/useGroupedPositions'
import { queryClient } from '@/config/query/queryClient'
import { routes } from '@/config/routes'
import { DASHBOARD_BREAKDOWN_TAB_COOKIE_KEY } from '@/constants/cookie'
import { useAccountContext } from '@/context/account'
import { useSettingsContext } from '@/context/settings'
import { useBorrowPositionsQuery } from '@/queries/borrowPositions.query'
import { usePositionsQuery } from '@/queries/positions.query'
import { rewardsQueryOptions, useRewardsQuery } from '@/queries/rewards.query'
import { shortenAddress } from '@/utils/address/shortenAddress'
import { getCookieValue } from '@/utils/cookie/cookie'
import { getMetaTags } from '@/utils/meta/getMetaTags'
import { addressSchema } from '@vaultsfyi/common/schemas'
import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { generatePath, redirect, useLoaderData } from 'react-router'
import type { Address } from 'viem'
import { z } from 'zod'

export const dashboardPageParamsSchema = z.object({
  address: addressSchema.optional(),
})

function parseBreakdownTabCookie(request: Request): BreakdownTab {
  const raw = getCookieValue(DASHBOARD_BREAKDOWN_TAB_COOKIE_KEY, request)
  if (!raw) return DEFAULT_BREAKDOWN_TAB
  try {
    const parsed = JSON.parse(raw)
    return BREAKDOWN_TAB_VALUES.includes(parsed) ? (parsed as BreakdownTab) : DEFAULT_BREAKDOWN_TAB
  } catch {
    return DEFAULT_BREAKDOWN_TAB
  }
}

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const parseResult = dashboardPageParamsSchema.safeParse(params)
  const address = parseResult.success ? (parseResult.data.address as Address) : undefined
  if (!address && params.address) {
    throw redirect(generatePath(routes.dashboard, { address: '' }), 302)
  }

  const initialRewards = address
    ? await queryClient.ensureQueryData(rewardsQueryOptions({ address })).catch(() => undefined)
    : undefined

  const initialBreakdownTab = parseBreakdownTabCookie(request)

  return { address, initialRewards, initialBreakdownTab }
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  const title = data?.address ? `${shortenAddress(data.address)} | Dashboard` : 'Dashboard'

  return getMetaTags({
    title: title,
    description:
      'Discover the best yield opportunities in DeFi. Manage and track onchain yield for Aave, Compound, Morpho, Fluid, Euler, and more.',
    url: location.pathname,
  })
}

export default function Dashboard() {
  const { address, initialRewards, initialBreakdownTab } = useLoaderData<typeof loader>()
  const { apyRange, includeRewards } = useSettingsContext()
  const { account } = useAccountContext()
  const trackedAddress = address ?? account
  const { data: positionsList } = usePositionsQuery({
    address: trackedAddress,
    apyInterval: apyRange,
  })
  const { data: borrowPositions } = useBorrowPositionsQuery({ address: trackedAddress })
  const positionsAboveThreshold = getPositionsAboveThreshold(positionsList)
  const lpTokensBalance = useGroupedPositions(trackedAddress, positionsAboveThreshold, includeRewards)
  const assetsBalance = useGroupedBalances({ address: trackedAddress })
  const { rewards } = useRewardsQuery({ address: trackedAddress, initialData: initialRewards })

  return (
    <DashboardPage
      address={trackedAddress ?? undefined}
      includeRewards={includeRewards}
      apyRange={apyRange}
      lpTokensBalance={lpTokensBalance}
      assetsBalance={assetsBalance}
      borrowPositions={borrowPositions}
      rewards={rewards}
      initialBreakdownTab={initialBreakdownTab}
      hideFunding
      editableAddress
    />
  )
}
